import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/Button.jsx';
import { useTenant } from '../../context/TenantContext.jsx';
import { verifyOrderPayment } from '../../lib/api.js';

export const CHECKOUT_VERIFY_RETRIES = 3;
export const CHECKOUT_VERIFY_DELAY_MS = 2000;

/**
 * Ruft verify auf; solange payment_status nicht 'paid' ist, bis zu `retries` Wiederholungen
 * im Abstand `delayMs`. Liefert { phase: 'paid'|'error', orderNumber }.
 * `wait` ist injizierbar (Tests ohne echte Wartezeit).
 */
export async function verifyWithRetry(verify, {
  retries = CHECKOUT_VERIFY_RETRIES,
  delayMs = CHECKOUT_VERIFY_DELAY_MS,
  wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  onPending = () => {},
} = {}) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await verify();
      if (res?.payment_status === 'paid') {
        return { phase: 'paid', orderNumber: res.order_number || null };
      }
    } catch {
      // Netzwerk-/Serverfehler zählen wie 'noch nicht bestätigt'.
    }
    if (attempt < retries) {
      onPending();
      await wait(delayMs);
    }
  }
  return { phase: 'error', orderNumber: null };
}

/**
 * Erfolgsseite nach Stripe-Checkout (a-047).
 * Ruft GET /api/orders/verify auf. Ist die Zahlung noch nicht bestätigt, wird bis zu
 * dreimal im Abstand von 2 s wiederholt. Danach oder bei Fehler: Hinweis zur Kontaktaufnahme.
 */
export function CheckoutSuccessView({ onNavigate, delayMs = CHECKOUT_VERIFY_DELAY_MS }) {
  const { tenant } = useTenant();
  const [state, setState] = useState({ phase: 'loading', orderNumber: null });

  useEffect(() => {
    const sessionId = typeof window !== 'undefined'
      ? new URL(window.location.href).searchParams.get('session_id')
      : null;
    if (!sessionId) {
      setState({ phase: 'error', orderNumber: null });
      return undefined;
    }

    let cancelled = false;
    verifyWithRetry(
      () => verifyOrderPayment(sessionId, tenant?.id),
      {
        delayMs,
        onPending: () => { if (!cancelled) setState({ phase: 'pending', orderNumber: null }); },
      }
    ).then((result) => {
      if (!cancelled) setState(result);
    });
    return () => { cancelled = true; };
  }, [tenant?.id, delayMs]);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-16 sm:px-6 lg:px-8 space-y-8 text-center" data-testid="checkout-success">
      {state.phase === 'paid' && (
        <>
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mx-auto">
            <CheckCircle2 className="h-10 w-10" />
          </div>
          <h1 className="font-display text-3xl font-extrabold text-[var(--foreground)]">
            Vielen Dank! Ihre Bestellung ist eingegangen.
          </h1>
          {state.orderNumber && (
            <p className="text-base text-[var(--secondary)]">
              Bestellnummer: <span className="font-mono font-bold text-[var(--foreground)]" data-testid="order-number">{state.orderNumber}</span>
            </p>
          )}
        </>
      )}

      {state.phase === 'loading' || state.phase === 'pending' ? (
        <div className="flex flex-col items-center gap-3 text-[var(--secondary)]" data-testid="checkout-pending">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Ihre Zahlung wird bestätigt. Bitte warten Sie einen Moment...</p>
        </div>
      ) : null}

      {state.phase === 'error' && (
        <>
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-50 text-red-600 mx-auto">
            <AlertCircle className="h-10 w-10" />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-[var(--foreground)]">
            Zahlung konnte nicht bestätigt werden — bitte kontaktieren Sie uns.
          </h1>
          <Button variant="outline" onClick={() => onNavigate?.('/kontakt')} className="gap-2 font-bold cursor-pointer">
            Zur Kontaktseite
            <ArrowRight className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
}
